import { Entity } from '@common/types/Entity';
import { EyeIcon, SwitchVerticalAIcon } from '@prophecy/icons';
import { BaseColor, theme } from '@prophecy/theme';
import {
  Stack,
  Ellipsis,
  ExcelTable,
  Text,
  Button,
  ColumnsType,
  TableActions,
  Badges,
  overlayStyle,
  Uploader,
  ProgressBar
} from '@prophecy/ui-v3';
import { TABLE_CONTAINER } from '@prophecy/ui-v3/Table/tokens';
import { StyledUploadContainer } from '@prophecy/ui-v3/Uploader/styled';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useImmer } from 'use-immer';

import { useGlobalVariables } from '../../../../context/globalConfig/securedConfigHook';
import { DeleteFile } from '../../components/DeleteFile';
import { MoveToFolder } from '../steps/components/MoveToFolder';
import { UploadFile } from '../steps/components/UploadFile';
import { FileTypes, TreeItem } from '../steps/types';
import { getTotalFolderCounts, getTreeDataWithExtension, PipelineExtensions } from '../steps/utils';
import { useImportState } from '../useImportState';
import { PsetContent } from './PsetContent';

const StyledStack = styled(Stack)`
  width: 100%;
  span {
    display: inline-grid;
  }
`;

const StyledProgressBar = styled(ProgressBar)`
  width: 96%;
  z-index: 0;
  position: absolute;
  top: 19px;
  left: ${theme.spaces.x12};
  border-radius: 0;
`;

const StyledButton = styled(Button)`
  &:disabled {
    cursor: not-allowed;
  }
` as unknown as typeof Button;
const StyledUploader = styled(Uploader)<{ readonly isUploading?: boolean }>`
  width: 100%;
  height: 100%;
  position: relative;
  margin-bottom: 0;
  ${StyledUploadContainer} {
    width: 100%;
    height: 100%;
    padding: 0;
  }
  .deleteFile > div {
    display: flex;
    text-align: right;
  }
  ${({ isUploading }) =>
    isUploading
      ? `
          .${TABLE_CONTAINER}{
    border-radius: 0 0 ${theme.radius.x8} ${theme.radius.x8};
  }
  `
      : ''}
`;

const StyledStackContainer = styled(Stack)`
  cursor: pointer;
  && table td:first-child {
    display: flex;
    flex-direction: row;
    gap: 4px;
    align-items: center;
    height: 100%;
  }
`;

export const ImportedFiles = ({ fileCount }: { fileCount: number }) => {
  const {
    isDeleting,
    uploadMutationState: { uploadFiles },
    transpileGraphState: {
      overallPercentage,
      clientState: {
        upload: { isUploading }
      }
    },
    browserDataLoading,
    loading,
    formDataWithActions: {
      formContext: { getValues }
    },
    transpilerState: { uploadedFilePaths }
  } = useImportState();
  const [movePanelMap, setToggleKeys] = useImmer<{ [key: string]: boolean }>({});
  const isJobEnabled = !!useGlobalVariables().isTranspilerJobEnabled;
  const source = getValues('source');

  const tableData = useMemo(
    () => getTreeDataWithExtension(uploadedFilePaths, source, isJobEnabled),
    [uploadedFilePaths, source, isJobEnabled]
  );
  const [contentPath, setContentPath] = useState('');

  const totalFolders = useMemo(() => getTotalFolderCounts(uploadedFilePaths), [uploadedFilePaths]);
  const togglePanel = (key: string, hasToggle: boolean) => {
    setToggleKeys((draft) => {
      if (hasToggle) {
        draft[key] = hasToggle;
      } else {
        delete draft[key];
      }
    });
  };

  const columns: ColumnsType<TreeItem> = [
    {
      key: 'title',
      dataIndex: 'title',
      title: 'File Name',
      render: (name, row: TreeItem) => {
        const textUI = (
          <Ellipsis
            tooltip={true}
            tooltipProps={{
              overlayStyle
            }}>
            {name}
          </Ellipsis>
        );
        const isLeaf = row.isLeaf;
        return isLeaf ? (
          <StyledStack direction='horizontal' alignY='center' gap={theme.spaces.x4}>
            {row.icon}
            <Text level='sm' tone={!row.selectable && isLeaf ? theme.colors.gray400 : theme.colors.gray900}>
              {textUI}
            </Text>
          </StyledStack>
        ) : (
          <StyledStack>{textUI}</StyledStack>
        );
      },
      filter: {
        type: 'search',
        searchInputProps: {
          placeholder: 'Search pipeline',
          'data-test-id': 'imported-file-name-search-input'
        }
      }
    },
    {
      title: <DeleteFile name={'all the files'} isAll={true} filePath={'/'} />,
      className: 'deleteFile',
      width: '110px',
      dataIndex: 'Delete',
      render: (_, row, _index: number) => {
        const fileType = row?.fileType as FileTypes;
        const isJobNode = fileType === FileTypes.plan;
        const isPipelineNode = PipelineExtensions.includes(fileType);
        const currentEntity = isPipelineNode || !row.isLeaf ? Entity.Pipeline : isJobNode ? Entity.Job : undefined;
        const path = row.key as string;
        return (
          <>
            {contentPath === path && contentPath && (
              <PsetContent path={contentPath} onClose={() => setContentPath('')} />
            )}
            {movePanelMap[path] && <MoveToFolder pathToMove={path} onClose={() => togglePanel(path, false)} />}
            {path && (
              <TableActions gap={theme.spaces.x2}>
                {row.selectable && (
                  <Button
                    variant='plain'
                    tooltip='View Content'
                    onClick={() => setContentPath(path)}
                    data-test-id='imported-file-view-content-button'>
                    <EyeIcon type='default' color={theme.colors.gray700} />
                  </Button>
                )}
                <StyledButton
                  variant='plain'
                  disabled={!totalFolders}
                  tooltip='Move'
                  onClick={() => togglePanel(path, true)}
                  data-test-id='imported-file-move-button'>
                  <SwitchVerticalAIcon
                    type='default'
                    color={totalFolders ? theme.colors.gray700 : theme.colors.gray300}
                  />
                </StyledButton>
                <DeleteFile entity={currentEntity} name={row.title as string} filePath={path} />
              </TableActions>
            )}
          </>
        );
      }
    }
  ];

  return (
    <StyledStackContainer gap={theme.spaces.x12} width='100%' grow='1'>
      <Stack direction='horizontal' align='space-between' alignY='center' width='100%'>
        <Stack direction='horizontal' align='space-between' alignY='center' gap={theme.spaces.x12}>
          <Text level='md' weight={theme.fontWeight.medium} tone={theme.colors.gray700}>
            Imported Files
          </Text>
          <Badges tone={BaseColor.gray} size='s'>
            {fileCount}
          </Badges>
        </Stack>
        <UploadFile isButton={true} />
      </Stack>
      <StyledUploader multiple={true} noClick={true} onDrop={uploadFiles} isUploading={isUploading}>
        {isUploading && <StyledProgressBar height='5px' variant={'loading'} value={overallPercentage} width='100%' />}
        <ExcelTable
          placeholderRows={10}
          fixedHeader={true}
          columns={columns}
          width='100%'
          loading={browserDataLoading || loading || isDeleting || isUploading}
          height='100%'
          virtualize={true}
          dataSource={tableData}
          data-test-id='transpiler-imported-file-table'
        />
      </StyledUploader>
    </StyledStackContainer>
  );
};
